import {useEffect, useRef} from "react";
import {ControlsContainer, useLoadGraph, useSigma} from "@react-sigma/core";
import {UndirectedGraph} from "graphology";
import {DEFAULT_NODE_SIZE} from "./EdgeGraph";

const MOUSE_EVENTS = new Set(['click', 'rightClick', 'doubleClick', 'wheel']);

export const useEventHandlers = ((handlers) => {
  const sigma = useSigma();
  useEffect(() => {
    Object.entries(handlers).forEach(([event, handler]) => {
      if (event.startsWith("touch")) {
        sigma.getTouchCaptor().on(event, handler);
      } else if (event.startsWith('mouse') || MOUSE_EVENTS.has(event)) {
        sigma.getMouseCaptor().on(event, handler);
      } else if (event === "updated") {
        sigma.camera.on(event, handler);
      } else {
        sigma.on(event, handler);
      }
    });
    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        if (event.startsWith("touch")) {
          sigma.getTouchCaptor().off(event, handler);
        } else if (event.startsWith('mouse') || MOUSE_EVENTS.has(event)) {
          sigma.getMouseCaptor().off(event, handler);
        } else if (event === "updated") {
          sigma.camera.off(event, handler);
        } else {
          sigma.off(event, handler);
        }
      });
    };
  }, [sigma, ...Object.keys(handlers).sort().map(k => handlers[k])]);
});

export const SigmaEdgeGraphLoader = ({
                                       nodes = [],
                                       adjacency = [],
                                       controlsPosition = null,
                                       width = 10,
                                       height = 10,
                                       deriveX = _ => 0,
                                       deriveY = _ => 0,
                                       currentState = null,
                                       setMinSimilarity = _ => {},
                                       setMaxSimilarity = _ => {},
                                       onRandomize = null,
                                       columnCount = 0,
                                       similarityColumn = -1,
                                       intermediateColumn = -1,
                                       showSimilarity = true,
                                       showIntermediate = true,
                                     }) => {

  const loadGraph = useLoadGraph();
  const sigma = useSigma();

  // Store initial camera state to prevent unwanted camera modifications
  const initialCameraStateRef = useRef(null);

  // IMPORTANT: This effect rebuilds the entire graph when nodes/adjacency changes.
  // For smooth animations to work, this should ONLY trigger when the actual data set changes
  // (new search results, new query), NOT when filtering/hiding nodes.
  //
  // The parent component (EdgeGraph) manages node visibility via visibleNodeIDs and
  // passes ALL nodes here, hiding them via nodeReducer. This preserves graph structure
  // continuity, allowing Sigma.js to animate position changes smoothly.
  useEffect(() => {
    const graph = new UndirectedGraph();
    nodes.forEach(node => graph.addNode(node.id, node));

    // Track edge loading for debugging
    let edgesAdded = 0;
    let edgesSkipped = 0;
    let indirectEdgesAdded = 0;
    let firstIndirectEdge = null;

    adjacency.forEach((neighbors, idx) => neighbors
      .forEach(([nbr, edgeAttributes]) => {
        if (nodes[idx] && nodes[nbr]) {
          // In undirected graph, edge A→B is same as B→A
          // Only add if edge doesn't exist to avoid overwriting attributes
          try {
            graph.addEdge(nodes[idx].id, nodes[nbr].id, edgeAttributes);
            edgesAdded++;

            // Track indirect edges
            if (edgeAttributes.isIndirect) {
              indirectEdgesAdded++;
              if (!firstIndirectEdge) {
                firstIndirectEdge = {
                  from: nodes[idx].id,
                  to: nodes[nbr].id,
                  attrs: edgeAttributes,
                };
              }
            }
          } catch (e) {
            // Edge already exists - this is expected in undirected graphs
            // Keep the first edge's attributes (don't overwrite)
            edgesSkipped++;
          }
          // Only process details for edges that have them (direct edges from server)
          // Indirect edges don't have details array
          if (nodes[idx].category === nodes[nbr].category && edgeAttributes.details) {
            nodes[nbr].score = Math.max(nodes[nbr].score || 0,
                                        edgeAttributes.details.reduce((max, e) => Math.max(max, e.score), 0));
          }
        }
      }));

    console.log('[SigmaLoader] Edge loading summary:', {
      edgesAdded,
      edgesSkipped,
      indirectEdgesAdded,
      totalEdgesInGraph: graph.size,
    });

    if (firstIndirectEdge) {
      console.log('[SigmaLoader] First indirect edge added to graph:', {
        from: firstIndirectEdge.from,
        to: firstIndirectEdge.to,
        isIndirect: firstIndirectEdge.attrs.isIndirect,
        label: firstIndirectEdge.attrs.label,
        collapsedCount: firstIndirectEdge.attrs.collapsedCount,
        allAttrKeys: Object.keys(firstIndirectEdge.attrs),
      });

      // Verify edge exists in graph and attributes are preserved
      try {
        const edgeId = graph.edge(firstIndirectEdge.from, firstIndirectEdge.to);
        const storedAttrs = graph.getEdgeAttributes(edgeId);
        console.log('[SigmaLoader] Verified edge attributes in graph:', {
          edgeId,
          isIndirect: storedAttrs.isIndirect,
          label: storedAttrs.label,
          allStoredKeys: Object.keys(storedAttrs),
        });
      } catch (e) {
        console.error('[SigmaLoader] Failed to retrieve edge from graph:', e.message);
      }
    }
    const [min, max] = nodes
      .filter(n => n.score)
      .reduce(([min, max], node) => [Math.min(min, node.score), Math.max(max, node.score)],
              [1, 0]);
    setMinSimilarity(min);
    setMaxSimilarity(max);

    // NOTE: Query node ranks are already calculated by degree in EdgeGraph.js columns useMemo
    // No need to recalculate here - nodes already have correct degree-based ranks

    // Set node sizes and initial positions using column-based layout
    // Only set positions if currentState is available (after first render)
    nodes.forEach(n => {
      n.size = DEFAULT_NODE_SIZE
               + Math.log2(graph.degree(n.id) || 1);
      graph.setNodeAttribute(n.id, "size", n.size);
      if (currentState) {
        graph.setNodeAttribute(n.id, "x", deriveX(n, currentState));
        graph.setNodeAttribute(n.id, "y", deriveY(n, currentState));
      } else {
        // Initial load before state ready - set placeholder positions
        // These will be updated by the animation system on first render
        graph.setNodeAttribute(n.id, "x", 0);
        graph.setNodeAttribute(n.id, "y", 0);
      }
    });

    // Add invisible anchor nodes to force graph bounds and prevent camera auto-adjustment
    // This prevents single-node selections from being pushed off-screen by Sigma's auto-fit
    if (nodes.length > 0) {
      const xMultiplier = width < height ? 1000 : 1000 * width / (height || 1);
      const yMultiplier = height < width ? 1000 : 1000 * height / (width || 1);

      // Calculate number of visible columns
      const isColumnHidden = (col) => {
        return col === similarityColumn && !showSimilarity
               || col === intermediateColumn && !showIntermediate;
      };
      let visibleColumnCount = 0;
      for (let col = 0; col < columnCount; col++) {
        if (!isColumnHidden(col)) {
          visibleColumnCount++;
        }
      }

      // RHS anchor: simulates an additional column to provide space for RHS labels
      // Always at 1.35x to simulate a consistent "phantom column" beyond the visible columns
      // When only 2 node columns visible (LHS, RHS), this creates a 3-column layout feel
      const anchorRhsId = '__anchor_rhs__';
      const anchorXMultiplier = 1.35;
      const anchorX = xMultiplier * anchorXMultiplier;
      const anchorY = yMultiplier * 0.5; // Middle of graph vertically

      graph.addNode(anchorRhsId, {
        id: anchorRhsId,
        column: -999, // Special column to identify as anchor
        label: '',
        rank: 0,
        size: 0.01, // Minimal size
        x: anchorX,
        y: anchorY,
        hidden: true,
      });
      graph.setNodeAttribute(anchorRhsId, "x", anchorX);
      graph.setNodeAttribute(anchorRhsId, "y", anchorY);
      graph.setNodeAttribute(anchorRhsId, "size", 0.01);

      // Top anchor: prevents camera from pushing nodes off top edge
      const anchorTopId = '__anchor_top__';
      const anchorTopX = xMultiplier * 0.5; // Center horizontally
      const anchorTopY = yMultiplier * 1.2; // Above top edge (Y increases upward in graph space)

      graph.addNode(anchorTopId, {
        id: anchorTopId,
        column: -999,
        label: '',
        rank: 0,
        size: 0.01,
        x: anchorTopX,
        y: anchorTopY,
        hidden: true,
      });
      graph.setNodeAttribute(anchorTopId, "x", anchorTopX);
      graph.setNodeAttribute(anchorTopId, "y", anchorTopY);
      graph.setNodeAttribute(anchorTopId, "size", 0.01);

      // Bottom anchor: prevents camera from pushing nodes off bottom edge
      const anchorBottomId = '__anchor_bottom__';
      const anchorBottomX = xMultiplier * 0.5; // Center horizontally
      const anchorBottomY = yMultiplier * -0.2; // Below bottom edge

      graph.addNode(anchorBottomId, {
        id: anchorBottomId,
        column: -999,
        label: '',
        rank: 0,
        size: 0.01,
        x: anchorBottomX,
        y: anchorBottomY,
        hidden: true,
      });
      graph.setNodeAttribute(anchorBottomId, "x", anchorBottomX);
      graph.setNodeAttribute(anchorBottomId, "y", anchorBottomY);
      graph.setNodeAttribute(anchorBottomId, "size", 0.01);
    }

    // Save camera state before loading graph (if we have one)
    const camera = sigma.getCamera();
    if (initialCameraStateRef.current === null) {
      // First load - capture initial state after Sigma auto-fits
      loadGraph(graph);
      sigma.refresh();
      // Capture the auto-fit camera state
      initialCameraStateRef.current = camera.getState();
    } else {
      // Subsequent loads - preserve camera position
      const savedState = camera.getState();
      loadGraph(graph);
      // Restore camera to prevent auto-adjustment
      camera.setState(savedState);
      sigma.refresh();
    }

    // Verify edges are still in Sigma's graph after loading
    const sigmaGraph = sigma.getGraph();
    console.log('[SigmaLoader] After loadGraph:', {
      nodesInSigma: sigmaGraph.order,
      edgesInSigma: sigmaGraph.size,
    });

    // Sample first few edges to verify attributes are preserved in Sigma
    let sampleCount = 0;
    let indirectCount = 0;
    sigmaGraph.forEachEdge((edgeId, attrs, source, target) => {
      if (attrs.isIndirect) {
        indirectCount++;
        if (indirectCount === 1) {
          console.log('[SigmaLoader] First indirect edge in Sigma graph:', {
            edgeId,
            source,
            target,
            isIndirect: attrs.isIndirect,
            label: attrs.label,
            collapsedCount: attrs.collapsedCount,
            allAttrKeys: Object.keys(attrs),
          });
        }
      }
      sampleCount++;
      if (sampleCount >= 50) return; // Only check first 50 edges
    });

    console.log('[SigmaLoader] Indirect edges in Sigma graph:', indirectCount);
  }, [loadGraph, nodes, adjacency, setMinSimilarity, setMaxSimilarity, deriveX, deriveY, sigma, width, height, columnCount, similarityColumn, intermediateColumn, showSimilarity, showIntermediate]);

  // Randomize node positions - they will animate back to their target positions via EdgeGraph's animation system
  const randomize = () => {
    // Trigger animation in parent component (which handles randomization in correct coordinate space)
    if (onRandomize) {
      onRandomize();
    }
  };

  // Reset camera to default view
  const resetCamera = () => {
    sigma.getCamera().setState({ x: 0.5, y: 0.5, angle: 0, ratio: 1 });
  };

  return controlsPosition && (
    <ControlsContainer position={controlsPosition}>
      <button className="glyphicon glyphicon-random" onClick={randomize} title="Randomize and animate to target positions"/>
      <button className="glyphicon glyphicon-screenshot" onClick={resetCamera} title="Reset camera view"/>
    </ControlsContainer>
  );
};

export default SigmaEdgeGraphLoader;
